import { plaidService, PlaidAccount } from "../external/plaidService";
import { isAbortError } from "../utils/abort";

export interface BankAccountBalance {
  accountId: string;
  itemId: string;
  name: string;
  officialName: string | null;
  mask: string;
  type: "checking" | "savings";
  balance: number;
  available: number | null;
  institution?: string | null;
  lastSynced: Date;
  isPlaidConnected: boolean;
}

export interface SyncResult {
  success: boolean;
  message: string;
  canSyncAt?: Date;
  remainingSeconds?: number;
}

// Cache configuration
const CACHE_KEY = "plaid_bank_accounts_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedData {
  data: BankAccountBalance[];
  timestamp: number;
}

export const plaidBankAccountService = {
  /**
   * Fetch bank accounts (checking and savings) from Plaid
   * Uses 5-minute cache to minimize API calls
   */
  getBankAccounts: async (
    forceRefresh: boolean = false
  ): Promise<BankAccountBalance[]> => {
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp }: CachedData = JSON.parse(cached);
            const age = Date.now() - timestamp;

            if (age < CACHE_DURATION) {
              return data.map((account) => ({
                ...account,
                lastSynced: new Date(account.lastSynced),
              }));
            }
          } catch (e) {
            // Cache parse error, will fetch fresh data
          }
        }
      }

      // Fetch fresh data from Plaid
      const accounts = await plaidService.getAccounts();

      // Filter for checking and savings accounts
      const bankAccounts = accounts.filter(
        (account: PlaidAccount) =>
          account.type === "depository" &&
          (account.subtype === "checking" || account.subtype === "savings")
      );

      // Convert to BankAccountBalance format
      const bankBalances: BankAccountBalance[] = bankAccounts.map(
        (account: PlaidAccount) => ({
          accountId: account.account_id,
          itemId: account.item_id,
          name: account.name,
          officialName: account.official_name,
          mask: account.mask,
          type: account.subtype as "checking" | "savings",
          balance: account.balances.current || 0,
          available: account.balances.available,
          institution: account.institution?.name || null,
          lastSynced: new Date(),
          isPlaidConnected: true,
        })
      );

      // Cache the results
      const cacheData: CachedData = {
        data: bankBalances,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));

      return bankBalances;
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] Plaid bank accounts aborted", error);
        return [];
      }
      console.error("Error fetching Plaid bank accounts:", error);
      return [];
    }
  },

  /**
   * Get total balance for all checking accounts
   */
  getTotalCheckingBalance: async (
    forceRefresh: boolean = false
  ): Promise<number> => {
    const accounts =
      await plaidBankAccountService.getBankAccounts(forceRefresh);
    return accounts
      .filter((a) => a.type === "checking")
      .reduce((sum, account) => sum + account.balance, 0);
  },

  /**
   * Get total balance for all savings accounts
   */
  getTotalSavingsBalance: async (
    forceRefresh: boolean = false
  ): Promise<number> => {
    const accounts =
      await plaidBankAccountService.getBankAccounts(forceRefresh);
    return accounts
      .filter((a) => a.type === "savings")
      .reduce((sum, account) => sum + account.balance, 0);
  },

  /**
   * Get checking accounts only
   */
  getCheckingAccounts: async (
    forceRefresh: boolean = false
  ): Promise<BankAccountBalance[]> => {
    const accounts =
      await plaidBankAccountService.getBankAccounts(forceRefresh);
    return accounts.filter((a) => a.type === "checking");
  },

  /**
   * Get savings accounts only
   */
  getSavingsAccounts: async (
    forceRefresh: boolean = false
  ): Promise<BankAccountBalance[]> => {
    const accounts =
      await plaidBankAccountService.getBankAccounts(forceRefresh);
    return accounts.filter((a) => a.type === "savings");
  },

  /**
   * Get the cache timestamp to check rate limiting
   */
  getCacheTimestamp: (): number | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { timestamp }: CachedData = JSON.parse(cached);
        return timestamp;
      }
    } catch (e) {
      // Cache parse error
    }
    return null;
  },

  /**
   * Check if sync is allowed based on rate limiting
   * Returns remaining time until sync is allowed
   */
  canSync: (): {
    allowed: boolean;
    remainingMs: number;
    canSyncAt: Date | null;
  } => {
    const timestamp = plaidBankAccountService.getCacheTimestamp();
    if (!timestamp) {
      return { allowed: true, remainingMs: 0, canSyncAt: null };
    }

    const age = Date.now() - timestamp;
    const remainingMs = Math.max(0, CACHE_DURATION - age);

    if (remainingMs === 0) {
      return { allowed: true, remainingMs: 0, canSyncAt: null };
    }

    const canSyncAt = new Date(timestamp + CACHE_DURATION);
    return { allowed: false, remainingMs, canSyncAt };
  },

  /**
   * Sync bank accounts - respects rate limiting
   * Returns success/failure with message about when sync will be available
   */
  syncAccounts: async (): Promise<SyncResult> => {
    const { allowed, remainingMs, canSyncAt } =
      plaidBankAccountService.canSync();

    if (!allowed && canSyncAt) {
      const remainingSeconds = Math.ceil(remainingMs / 1000);
      const remainingMinutes = Math.ceil(remainingSeconds / 60);

      let message: string;
      if (remainingMinutes > 1) {
        message = `Cannot sync again for ${remainingMinutes} minutes`;
      } else if (remainingSeconds > 60) {
        message = `Cannot sync again until ${canSyncAt.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })}`;
      } else {
        message = `Cannot sync again for ${remainingSeconds} seconds`;
      }

      return {
        success: false,
        message,
        canSyncAt,
        remainingSeconds,
      };
    }

    try {
      // Force refresh from Plaid
      await plaidBankAccountService.getBankAccounts(true);
      return {
        success: true,
        message: "Accounts synced successfully",
      };
    } catch (error) {
      console.error("Error syncing bank accounts:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to sync accounts",
      };
    }
  },
};
